import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { OverflowMenu, OverflowMenuItem } from '@carbon/react';
import FormattedDate from 'src/components/Base/FormattedDate';
import { IssueTypeTag } from 'src/components/Issue/IssueTags';
import { useMoney } from 'src/hooks/useMoney';
import { IssueEntity } from 'src/services/apis/types';
import { employeeName } from 'src/utils/employee';

interface IssueCardProps {
  issue: IssueEntity;
  canWrite: boolean;
  onView: (issue: IssueEntity) => void;
  onEdit: (issue: IssueEntity) => void;
  onDelete: (issue: IssueEntity) => void;
}

/**
 * The whole card is the drag handle (when the caller may write), and a click that is not a drag
 * opens the details. The pointer sensor's 4px activation distance is what tells the two apart.
 */
const IssueCard = React.memo(({ issue, canWrite, onView, onEdit, onDelete }: IssueCardProps) => {
  const { t } = useTranslation();
  // The SAME formatter the table's `display: { kind: 'currency' }` column uses — CurrencyCell
  // calls this out of the crud config (columnDisplays.tsx:58), which is the portal's useMoney
  // (CrudProviders.tsx). Re-deriving the format here would drift and would drop its null guard.
  const { format: formatCurrency } = useMoney();
  // `disabled` is what makes a read-only board genuinely read-only: dnd-kit attaches no
  // pointer or keyboard sensors to a disabled sortable, so there is nothing to lift.
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: issue.id,
    disabled: !canWrite,
  });

  // The card is also the activator node, so the keyboard sensor only lifts on a key pressed ON
  // the card — not on Enter in the overflow menu inside it, which bubbles up to the same handler.
  const setRefs = (node: HTMLLIElement | null) => {
    setNodeRef(node);
    setActivatorNodeRef(node);
  };

  const handleClick = (event: React.MouseEvent<HTMLLIElement>) => {
    const target = event.target as HTMLElement;
    // React bubbles clicks through portals, so a click on the overflow menu's floating options
    // (rendered outside the card) arrives here too — as does one on the menu button itself.
    if (!event.currentTarget.contains(target) || target.closest('.cds--overflow-menu')) {
      return;
    }
    onView(issue);
  };

  return (
    // The card's keyboard path is the sortable's own (space lifts); the overflow menu inside it
    // carries "View details" for keyboard users, so the click here is a pointer shortcut.
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <li
      ref={setRefs}
      className={`issue-card${canWrite ? ' issue-card--draggable' : ''}${isDragging ? ' issue-card--dragging' : ''}`}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      onClick={handleClick}
      {...(canWrite
        ? { ...attributes, ...listeners, 'aria-label': `${t('issueBoard.dragHandle')}: ${issue.title}` }
        : {})}
    >
      <div className={'issue-card__header'}>
        <div className={'issue-card__tags'}>
          <IssueTypeTag type={issue.type} />
          <span className={'issue-card__fine'}>{formatCurrency(issue.fine)}</span>
        </div>
        <OverflowMenu
          size="sm"
          flipped
          align="bottom-right"
          iconDescription={t('actions.title')}
          className={'issue-card__menu'}
        >
          <OverflowMenuItem itemText={t('issueBoard.viewDetails')} onClick={() => onView(issue)} />
          {canWrite && <OverflowMenuItem itemText={t('actions.edit')} onClick={() => onEdit(issue)} />}
          {canWrite && (
            <OverflowMenuItem isDelete hasDivider itemText={t('actions.delete')} onClick={() => onDelete(issue)} />
          )}
        </OverflowMenu>
      </div>
      <span className={'issue-card__title'}>{issue.title}</span>
      <div className={'issue-card__footer'}>
        <span>{employeeName(issue.employee) || t('common.noEmployee')}</span>
        <FormattedDate date={issue.createdAt} format={'DD/MM/YYYY'} />
      </div>
    </li>
  );
});

IssueCard.displayName = 'IssueCard';

export default IssueCard;
